import React from 'react';
import {
  Control,
  FieldError,
  Controller,
  Merge,
  FieldErrorsImpl,
} from 'react-hook-form';
import ReactHtmlParser from 'react-html-parser';
import { useIntl } from 'react-intl';
import ReCAPTCHA from 'react-google-recaptcha';

import { RecaptchaSize } from '../../../types';
import {
  Button,
  CheckboxLabel,
  DropdownWrapper,
  ErrorMsg,
  FieldSet,
  InputField,
  Spinner,
} from '../styles';
import {
  FormFieldCssClassType,
  FormFieldI,
  FormFieldInputType,
  FormFieldType,
} from '../types';
import FormDropdown from '../FormDropdown/FormDropdown';
import IconButton from '../../IconButton/IconButton';
import updateActiveDropdown from '../../../utils/updateActiveDropdown';

interface Props {
  formField: FormFieldI;
  register: (fieldName: string) => Record<string, any>;
  control?: Control;
  recaptchaRef?: React.RefObject<unknown>;
  recaptchaSize?: RecaptchaSize;
  isFormValid?: boolean;
  loading?: boolean;
  errorSection?: Record<string, any>;
  valid?: boolean;
  error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>>;
  activeDropdown?: string | null;
  setActiveDropdown?: React.Dispatch<React.SetStateAction<string | null>>;
  onDropdownChange?: (...args: any[]) => any;
  selectedValue?: string;
}
const FormField = ({
  formField,
  register,
  control,
  recaptchaRef,
  recaptchaSize = RecaptchaSize.NORMAL,
  isFormValid,
  loading,
  errorSection,
  valid,
  error,
  activeDropdown,
  setActiveDropdown,
  onDropdownChange,
  selectedValue,
}: Props) => {
  const { locale } = useIntl();
  const {
    className,
    fieldName,
    displayLabel,
    inputType,
    maxLength,
    minLength,
    placeholderLabel,
    showError,
    errorMessage,
    type,
    fieldValues,
  } = formField;
  const errorLabel = `${String.fromCharCode(0x2718)} ${ReactHtmlParser(errorMessage)}`;

  if (type === FormFieldType.CHECKBOX) {
    return (
      <FieldSet className={FormFieldCssClassType.OPTIN}>
        <CheckboxLabel htmlFor={fieldName}>
          <InputField
            type="checkbox"
            name={fieldName}
            id={fieldName}
            data-testid={fieldName}
            {...(fieldName ? register(fieldName) : {})}
          />
          <span className="checkmark" />
          {ReactHtmlParser(displayLabel)}
        </CheckboxLabel>
      </FieldSet>
    );
  }
  if (type === FormFieldType.RECAPTCHA) {
    return (
      <FieldSet className={FormFieldCssClassType.RECAPTCHA}>
        <Controller
          control={control}
          name="gRecaptchaResponse"
          render={({ field: { onChange, value } }) => (
            <ReCAPTCHA
              id="gRecaptchaResponse"
              ref={recaptchaRef}
              lang={locale}
              sitekey={process.env.GATSBY_RECAPTCHA_KEY}
              // For dev testing use the below key from Google docs
              // sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
              onChange={onChange}
              name="gRecaptchaResponse"
              value={value}
              size={recaptchaSize}
            />
          )}
        />
        {error
          && (
          <ErrorMsg>
            {errorLabel}
          </ErrorMsg>
          )}
      </FieldSet>
    );
  }
  if (type === FormFieldType.SUBMIT) {
    const submitErrorLabel = `${String.fromCharCode(0x2718)} ${ReactHtmlParser(errorSection?.errorMessage)}`;
    return (
      <FieldSet className={FormFieldCssClassType.SUBMIT}>
        <Button
          id="formContactSubmit"
          type={FormFieldInputType.SUBMIT}
          disabled={!isFormValid}
        >
          {loading ? (
            <Spinner data-testid="form-field-submit-spinner" />
          ) : (
            ReactHtmlParser(displayLabel)
          )}
        </Button>
        {error && errorSection && (
          <ErrorMsg>
            {submitErrorLabel}
          </ErrorMsg>
        )}
      </FieldSet>
    );
  }

  if (type === FormFieldType.DROPDOWN) {
    const isActive = fieldValues ? fieldValues?.length > 0 : false;
    return (
      <FieldSet className={className}>
        <label htmlFor={fieldName}>{ReactHtmlParser(displayLabel)}</label>
        <DropdownWrapper selectedValue={selectedValue}>
          <IconButton
            aria-haspopup="true"
            aria-expanded={fieldName === activeDropdown ? 'true' : 'false'}
            active={isActive}
            icon="greyArrow"
            onClick={(e) => {
              e.preventDefault();
              if (setActiveDropdown) {
                updateActiveDropdown({
                  dropdown: fieldName,
                  activeDropdown,
                  setActiveDropdown,
                });
              }
            }}
            dataTestId="form-dropdown-button"
          >
            {selectedValue ?? placeholderLabel}
          </IconButton>
          <FormDropdown
            formField={formField}
            active={formField.fieldName === activeDropdown}
            onDropdownChange={onDropdownChange}
            selectedValue={selectedValue}
            setActiveDropdown={setActiveDropdown}
          />
        </DropdownWrapper>
      </FieldSet>
    );
  }
  return (
    <FieldSet className={className}>
      <label htmlFor={fieldName}>{ReactHtmlParser(displayLabel)}</label>
      <InputField
        type={inputType ?? 'input'}
        name={fieldName}
        id={fieldName}
        maxLength={maxLength}
        minLength={minLength}
        placeholder={
          placeholderLabel ? ReactHtmlParser(placeholderLabel) : undefined
        }
        valid={valid}
        error={showError ? (error as FieldError) : undefined}
        data-testid={fieldName}
        {...(fieldName ? register(fieldName) : {})}
      />
      {showError && error && (
        <ErrorMsg>
          {errorLabel}
        </ErrorMsg>
      )}
    </FieldSet>
  );
};

export default FormField;
